/**
 * EditRateDialog Component
 * 
 * Dialog for editing currency exchange rate.
 */

import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stack,
  Alert,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Currency } from "../../../types";

const RATE_DECIMAL_PLACES = 5;
const roundRateToDecimals = (value: number): number =>
  Math.round(value * 10 ** RATE_DECIMAL_PLACES) / 10 ** RATE_DECIMAL_PLACES;

export interface EditRateDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: () => Promise<void>;
  editingCurrency: Currency | null;
  editCurrencyRate: string;
  setEditCurrencyRate: (value: string) => void;
  defaultCurrency: string;
  formatRate: (rate: number | string) => string;
}

/**
 * Dialog for editing currency exchange rate
 */
export default function EditRateDialog({
  open,
  onClose,
  onUpdate,
  editingCurrency,
  editCurrencyRate,
  setEditCurrencyRate,
  defaultCurrency,
  formatRate,
}: EditRateDialogProps): React.ReactElement {
  const rateUnchanged = useMemo(() => {
    if (!editingCurrency?.rate || !editCurrencyRate?.trim()) return false;
    const parsed = parseFloat(editCurrencyRate);
    if (isNaN(parsed) || parsed <= 0) return false;
    const originalDisplayRate = 1 / editingCurrency.rate!;
    return roundRateToDecimals(parsed) === roundRateToDecimals(originalDisplayRate);
  }, [editingCurrency?.rate, editCurrencyRate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">
            Edit Exchange Rate - {editingCurrency?.code}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Currency code cannot be changed. Only the exchange rate can be updated.
          </Alert>
          <TextField
            required
            label={`Exchange Rate (1 ${editingCurrency?.code || "XXX"} = ? ${defaultCurrency})`}
            type="number"
            inputProps={{ step: "0.00001", min: "0.00001" }}
            value={editCurrencyRate}
            onChange={(e) => setEditCurrencyRate(e.target.value)}
            fullWidth
            placeholder={editingCurrency?.rate === null ? "Enter rate (e.g., 0.0025)" : "0.0025"}
            helperText={
              editingCurrency && editCurrencyRate && !isNaN(parseFloat(editCurrencyRate)) && parseFloat(editCurrencyRate) > 0
                ? `1 ${editingCurrency.code} = ${formatRate(parseFloat(editCurrencyRate))} ${defaultCurrency}`
                : editingCurrency?.rate === null
                ? "Rate not set. Please enter a rate to use this currency."
                : `Enter how many ${defaultCurrency} = 1 ${editingCurrency?.code || "XXX"}`
            }
          />
          {editingCurrency && editCurrencyRate && !isNaN(parseFloat(editCurrencyRate)) && parseFloat(editCurrencyRate) > 0 && (
            <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 1, border: 1, borderColor: "divider" }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Live Conversion Preview:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                1 {editingCurrency.code} = {formatRate(parseFloat(editCurrencyRate))} {defaultCurrency}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onUpdate}
          disabled={!editCurrencyRate || isNaN(parseFloat(editCurrencyRate)) || parseFloat(editCurrencyRate) <= 0 || rateUnchanged}
          sx={{ flex: 1, py: 1.5 }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
